import { supabase } from '@/lib/supabase';

export interface StatementEntry {
  id: string;
  date: string;
  description: string;
  type: string;
  amount: number;
  recordedBalance: number;
  reference: string | null;
}
export interface CashStatement {
  account: {
    id: string;
    name: string;
    type: string;
    currentBalance: number;
    openingBalance: number;
    updatedAt: string;
  };
  exportedAt: string;
  entries: StatementEntry[];
  moneyIn: number;
  moneyOut: number;
  netMovement: number;
}
function amount(value: unknown): number {
  if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value)))
    throw new Error('Account amounts are unavailable. Refresh and try exporting again.');
  const result = Math.round(Number(value) * 100);
  if (!Number.isSafeInteger(result))
    throw new Error('An amount is too large to export accurately.');
  return result / 100;
}
function checkAbort(signal: AbortSignal) {
  if (signal.aborted) throw new DOMException('Statement export cancelled.', 'AbortError');
}
async function accountRecord(id: string, organizationId: string, signal: AbortSignal) {
  checkAbort(signal);
  const { data, error } = await supabase
    .from('cash_accounts')
    .select('id,organization_id,name,account_type,opening_balance,current_balance,updated_at')
    .eq('id', id)
    .eq('organization_id', organizationId)
    .eq('is_active', true)
    .abortSignal(signal)
    .single();
  checkAbort(signal);
  if (error) throw new Error('The selected account could not be read. Refresh and try again.');
  if (!data || data.id !== id || data.organization_id !== organizationId)
    throw new Error('The selected account is no longer available.');
  return {
    id: data.id,
    name: data.name,
    type: data.account_type,
    currentBalance: amount(data.current_balance),
    openingBalance: amount(data.opening_balance),
    updatedAt: data.updated_at,
  };
}
/** Full existing authorized ledger, independent of the recent-200 UI cache. */
export async function loadCashStatement(
  accountId: string,
  organizationId: string,
  signal: AbortSignal,
  progress: (loaded: number, total: number) => void,
): Promise<CashStatement> {
  if (!accountId || !organizationId) throw new Error('Select an account before exporting.');
  for (let attempt = 0; attempt < 2; attempt++) {
    const before = await accountRecord(accountId, organizationId, signal);
    const entries: StatementEntry[] = [];
    const seen = new Set<string>();
    let expected: number | undefined;
    progress(0, 0);
    do {
      checkAbort(signal);
      const { data, error, count } = await supabase
        .from('cash_transactions')
        .select(
          'id,organization_id,account_id,transaction_date,created_at,particular,transaction_type,amount,balance_after,reference_id,is_void',
          { count: 'exact' },
        )
        .eq('organization_id', organizationId)
        .eq('account_id', accountId)
        .eq('is_void', false)
        .order('transaction_date', { ascending: true })
        .order('created_at', { ascending: true })
        .order('id', { ascending: true })
        .range(entries.length, entries.length + 499)
        .abortSignal(signal);
      checkAbort(signal);
      if (error)
        throw new Error(
          'Statement entries could not be loaded. No file was created. Please retry.',
        );
      if (count === null || (expected !== undefined && expected !== count))
        throw new Error('The ledger changed while loading. Please export again.');
      expected = count;
      for (const row of data ?? []) {
        if (
          seen.has(row.id) ||
          row.organization_id !== organizationId ||
          row.account_id !== accountId ||
          row.is_void
        )
          throw new Error('The statement changed while loading. Please export again.');
        seen.add(row.id);
        if (
          !/^\d{4}-\d{2}-\d{2}$/.test(row.transaction_date) ||
          new Date(`${row.transaction_date}T00:00:00Z`).toISOString().slice(0, 10) !==
            row.transaction_date
        )
          throw new Error('A transaction date is invalid. Refresh the ledger before exporting.');
        entries.push({
          id: row.id,
          date: row.transaction_date,
          description: row.particular || 'No description recorded',
          type: row.transaction_type,
          amount: amount(row.amount),
          recordedBalance: amount(row.balance_after),
          reference: row.reference_id,
        });
      }
      if (entries.length > expected || (!data?.length && entries.length < expected))
        throw new Error('The statement is incomplete. Please export again.');
      progress(entries.length, expected);
    } while (entries.length < expected!);
    const after = await accountRecord(accountId, organizationId, signal);
    if (
      before.updatedAt !== after.updatedAt ||
      before.currentBalance !== after.currentBalance ||
      before.openingBalance !== after.openingBalance
    ) {
      if (attempt === 0) continue;
      throw new Error('The account changed while preparing the statement. Please retry.');
    }
    const incoming = entries.reduce(
      (sum, entry) => sum + Math.max(0, Math.round(entry.amount * 100)),
      0,
    );
    const outgoing = entries.reduce(
      (sum, entry) => sum + Math.max(0, -Math.round(entry.amount * 100)),
      0,
    );
    if (!Number.isSafeInteger(incoming) || !Number.isSafeInteger(outgoing))
      throw new Error('Statement totals are too large to export accurately.');
    return {
      account: after,
      exportedAt: new Date().toISOString(),
      entries,
      moneyIn: incoming / 100,
      moneyOut: outgoing / 100,
      netMovement: (incoming - outgoing) / 100,
    };
  }
  throw new Error('The account changed. Please retry.');
}
